/**
 * Updates Module
 * 
 * Handles git status checking and server deployment.
 */

import { api, GitInfo } from './api';

// State
let currentGit: GitInfo | null = null;
let isChecking = false;
let isDeploying = false;

/**
 * Initialize updates panel.
 */
export function initUpdates(): void {
    const checkBtn = document.getElementById('check-updates-btn');
    const deployBtn = document.getElementById('deploy-btn');

    if (checkBtn) {
        checkBtn.addEventListener('click', checkForUpdates);
    }

    if (deployBtn) {
        deployBtn.addEventListener('click', deployUpdate);
    }

    // Initial check
    checkForUpdates();
}

/**
 * Check for available updates by comparing with remote.
 */
async function checkForUpdates(): Promise<void> {
    if (isChecking) return;

    const checkBtn = document.getElementById('check-updates-btn') as HTMLButtonElement;
    const deployBtn = document.getElementById('deploy-btn') as HTMLButtonElement;
    const updateAvailable = document.getElementById('update-available');

    try {
        isChecking = true;
        if (checkBtn) {
            checkBtn.disabled = true;
            checkBtn.textContent = 'Checking...';
        }

        // Fetch latest git status (includes behind_remote if available)
        currentGit = await api.getGitStatus();

        // Update current version display
        const currentCommit = document.getElementById('current-commit');
        const currentMessage = document.getElementById('current-message');

        if (currentCommit) {
            currentCommit.textContent = currentGit.commit_hash.slice(0, 8);
        }
        if (currentMessage) {
            currentMessage.textContent = currentGit.commit_message || '—';
        }

        // Check if update available
        const behindRemote = currentGit.behind_remote ?? 0;
        const hasUpdate = behindRemote > 0;

        if (updateAvailable) {
            updateAvailable.classList.toggle('hidden', !hasUpdate);
            const details = document.getElementById('update-details');
            if (details && hasUpdate) {
                details.textContent = `${behindRemote} commit(s) behind remote`;
            }
        }

        if (deployBtn) {
            deployBtn.disabled = !hasUpdate;
        }

        // Update sidebar badge
        const badge = document.getElementById('update-badge');
        if (badge) {
            badge.classList.toggle('hidden', !hasUpdate);
        }
    } catch (err) {
        console.error('Failed to check updates:', err);
    } finally {
        isChecking = false;
        if (checkBtn) {
            checkBtn.disabled = false;
            checkBtn.textContent = 'Check for Updates';
        }
    }
}

/**
 * Deploy update (git pull + restart).
 */
async function deployUpdate(): Promise<void> {
    if (isDeploying) return;


    const updateActions = document.getElementById('update-actions');
    const deployProgress = document.getElementById('deploy-progress');
    const deployStatus = document.getElementById('deploy-status');

    try {
        isDeploying = true;

        // Show progress
        if (updateActions) updateActions.classList.add('hidden');
        if (deployProgress) deployProgress.classList.remove('hidden');
        if (deployStatus) deployStatus.textContent = 'Pulling latest changes...';

        // Call deploy endpoint
        const result = await api.deploy();

        if (result.success) {
            if (deployStatus) {
                deployStatus.textContent = result.restarting
                    ? 'Restarting server... Page will reload shortly.'
                    : 'Update complete!';
            }

            // If restarting, poll until server comes back
            if (result.restarting) {
                await waitForRestart();
            }
        } else {
            throw new Error(result.error || 'Deploy failed');
        }
    } catch (err) {
        console.error('Deploy failed:', err);
        if (deployStatus) {
            deployStatus.textContent = `Error: ${err instanceof Error ? err.message : 'Deploy failed'}`;
        }
        // Show actions again after error
        setTimeout(() => {
            if (updateActions) updateActions.classList.remove('hidden');
            if (deployProgress) deployProgress.classList.add('hidden');
        }, 3000);
    } finally {
        isDeploying = false;
    }
}

/**
 * Poll server health until it comes back online after restart.
 */
async function waitForRestart(): Promise<void> {
    const deployStatus = document.getElementById('deploy-status');
    const maxAttempts = 30;
    const pollInterval = 2000;

    for (let i = 0; i < maxAttempts; i++) {
        await sleep(pollInterval);

        try {
            await api.getHealth();
            // Server is back!
            if (deployStatus) {
                deployStatus.textContent = 'Server restarted! Reloading...';
            }
            await sleep(1000);
            window.location.reload();
            return;
        } catch {
            // Still restarting
            if (deployStatus) {
                deployStatus.textContent = `Waiting for server... (${i + 1}/${maxAttempts})`;
            }
        }
    }

    // Timeout
    if (deployStatus) {
        deployStatus.textContent = 'Server taking too long to restart. Please refresh manually.';
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}
